/**
 * Validator Registry v2.0
 *
 * Centralized validation logic for all question types.
 * Separated from questionTypeRegistry for clean architecture.
 *
 * Part of the Content domain (DDD Fase 3).
 * Moved from src/config/validatorRegistry.ts in R25.
 */

import { Question, ScheduleEvent, MoneyDenom } from '../types'
import { ValidationResult } from '../../practice/logic/answerProtocol'
import { error as logError } from '../../utils/logger'
export type ValidatorFn = (
  userAnswer: string | number,
  question: Question,
) => ValidationResult

// ============================================================
// INDIVIDUAL VALIDATORS
// ============================================================

const validateMultipleChoice: ValidatorFn = (userAnswer, question) => ({
  isCorrect: Number(userAnswer) === question.correctAnswer,
  distractorIndex: Number(userAnswer),
})
const validateColumnArithmetic: ValidatorFn = (userAnswer, question) => {
  const normalizedUser = String(userAnswer).replace(/\./g, '')

  // For cijferend_rekenen: correctAnswer is a string (e.g. "3.834"),
  // not an index into options (which is empty []).
  // Compute correct answer from data.operand1/operand2 or fall back to correctAnswer string.
  let correctAnswer: string
  if (
    question.data?.operand1 !== undefined &&
    question.data?.operand2 !== undefined
  ) {
    const op1 = Number(question.data.operand1)
    const op2 = Number(question.data.operand2)
    const op = question.data?.operation
    const isSubtraction = op === '-'
    const isMultiplication = op === '×' || op === '*'
    const result = isMultiplication
      ? op1 * op2
      : isSubtraction
        ? op1 - op2
        : op1 + op2
    correctAnswer = String(result)
  } else if (
    question.options &&
    question.options.length > 0 &&
    typeof question.correctAnswer === 'number'
  ) {
    correctAnswer = question.options[question.correctAnswer].replace(/\./g, '')
  } else {
    // correctAnswer is a string like "3.834"
    correctAnswer = String(question.correctAnswer).replace(/\./g, '')
  }

  const isCorrect = normalizedUser === correctAnswer
  let distractorIndex = 0
  if (question.options && question.options.length > 0) {
    distractorIndex = question.options.findIndex(
      (opt) => opt.replace(/\./g, '') === normalizedUser,
    )
    if (distractorIndex === -1) distractorIndex = 0
  }
  return {
    isCorrect,
    distractorIndex,
    displayAnswer: String(userAnswer),
  }
}
const validateLongDivision: ValidatorFn = (userAnswer, question) => {
  const normalizedUser = String(userAnswer).replace(/\./g, '')

  let correctAnswer: string
  if (
    question.data?.operand1 !== undefined &&
    question.data?.operand2 !== undefined
  ) {
    const op1 = Number(question.data.operand1)
    const op2 = Number(question.data.operand2)
    correctAnswer = String(Math.floor(op1 / op2))
  } else if (
    question.options &&
    question.options.length > 0 &&
    typeof question.correctAnswer === 'number'
  ) {
    correctAnswer = question.options[question.correctAnswer].replace(/\./g, '')
  } else {
    correctAnswer = String(question.correctAnswer).replace(/\./g, '')
  }

  const isCorrect = normalizedUser === correctAnswer
  let distractorIndex = 0
  if (question.options && question.options.length > 0) {
    distractorIndex = question.options.findIndex(
      (opt) => opt.replace(/\./g, '') === normalizedUser,
    )
    if (distractorIndex === -1) distractorIndex = 0
  }

  return {
    isCorrect,
    distractorIndex,
    displayAnswer: String(userAnswer),
  }
}
function formatMoneyDisplay(cents: number): string {
  const euros = Math.floor(cents / 100)
  const remainingCents = cents % 100
  if (remainingCents === 0) return `€${euros},-`
  return `€${euros},${remainingCents.toString().padStart(2, '0')}`
}

// NEW: Clean clock validator with rich feedback (MeesterJay 4.1)
const validateClock: ValidatorFn = (userAnswer, question) => {
  // Parse user answer (HH:MM format)
  const userStr = String(userAnswer).trim()
  const [userHoursStr, userMinutesStr] = userStr.split(':')
  const userHours = parseInt(userHoursStr, 10) || 0
  const userMinutes = parseInt(userMinutesStr, 10) || 0

  // CANONICAL: Read ONLY from data.clock (no fallback)
  if (!question.data?.clock) {
    logError('validateClock: Missing clock config in question.data.clock')
    return {
      isCorrect: false,
      distractorIndex: 0,
      displayAnswer: userStr,
      errorType: 'MISSING_CONFIG',
    }
  }
  const clockConfig = question.data.clock
  const correctHours = clockConfig?.target?.hours || 0
  const correctMinutes = clockConfig?.target?.minutes || 0
  const isCorrect = userHours === correctHours && userMinutes === correctMinutes

  // Calculate delta in minutes (handle 24h wrap-around)
  const userTotalMinutes = userHours * 60 + userMinutes
  const correctTotalMinutes = correctHours * 60 + correctMinutes

  // Calculate shortest distance (considering wrap-around)
  const directDelta = Math.abs(userTotalMinutes - correctTotalMinutes)
  const wrapDelta = 1440 - directDelta // 1440 = 24 * 60
  const delta = Math.min(directDelta, wrapDelta)

  // Determine error type for didactic feedback
  let errorType: string | undefined
  if (!isCorrect) {
    if (userHours !== correctHours && userMinutes === correctMinutes) {
      errorType = 'HOUR_OFF'
    } else if (userHours === correctHours && userMinutes !== correctMinutes) {
      if (delta === 5) errorType = 'MINUTE_OFF_5'
      else if (delta === 10) errorType = 'MINUTE_OFF_10'
      else if (delta === 15) errorType = 'MINUTE_OFF_15'
      else if (delta === 30) errorType = 'HALF_CONFUSION'
      else errorType = 'MINUTE_OFF'
    } else if (
      Math.abs(userHours - correctHours) === 12 &&
      userMinutes === correctMinutes
    ) {
      // FORMAT_24H: Only if hours differ by exactly 12 AND minutes match
      errorType = 'FORMAT_24H'
    } else {
      errorType = 'BOTH_OFF'
    }
  }

  // Find distractor index
  let distractorIndex = question.options.findIndex((opt) => opt === userStr)
  if (distractorIndex === -1) distractorIndex = 0
  return {
    isCorrect,
    distractorIndex,
    displayAnswer: userStr,
    correctAnswerDisplay: `${correctHours.toString().padStart(2, '0')}:${correctMinutes.toString().padStart(2, '0')}`,
    errorType,
    delta,
  }
}

// NEW: Clean money validator with rich feedback + constraint enforcement (MeesterJay 4.1)
const validateMoney: ValidatorFn = (userAnswer, question) => {
  // Parse user answer (token set format)
  let userTokens: Array<{
    valueCents: number
    count: number
  }> = []
  let userTotalCents = 0
  try {
    userTokens = JSON.parse(String(userAnswer))
    if (!Array.isArray(userTokens)) {
      throw new Error('Expected an array of money tokens')
    }
    if (
      !userTokens.every(
        (token) =>
          Number.isFinite(token?.valueCents) && Number.isFinite(token?.count),
      )
    ) {
      throw new Error(
        'Each money token must include finite valueCents and count numbers',
      )
    }
    // FILTER: Remove tokens with count <= 0 immediately
    userTokens = userTokens.filter((t) => t.count > 0)
    userTotalCents = userTokens.reduce(
      (sum, t) => sum + t.valueCents * t.count,
      0,
    )
  } catch {
    throw new Error(
      'Legacy money payload detected: expected canonical JSON token-set payload (e.g. [{"valueCents":50,"count":2}])',
    )
  }

  // CANONICAL: Read ONLY from data.money (no fallback)
  if (!question.data?.money) {
    logError('validateMoney: Missing money config in question.data.money')
    return {
      isCorrect: false,
      distractorIndex: 0,
      displayAnswer: formatMoneyDisplay(userTotalCents),
      errorType: 'MISSING_CONFIG',
    }
  }
  const moneyConfig = question.data.money
  const targetCents = moneyConfig?.targetCents || 0
  const allowedDenoms = moneyConfig?.allowedDenoms || []
  const constraints = moneyConfig.constraints || {}

  // CONSTRAINT VALIDATION
  const violations: string[] = []

  // 1. Check allowedDenoms - all tokens must be in the allowed list (only check count > 0)
  const allowedValues = new Set(
    allowedDenoms.map((d: MoneyDenom) => d.valueCents),
  )
  for (const token of userTokens) {
    if (!allowedValues.has(token.valueCents)) {
      violations.push(`INVALID_DENOM_${token.valueCents}`)
    }
  }

  // 2. Check token counts are valid (non-negative integers) - already filtered count <= 0
  for (const token of userTokens) {
    if (!Number.isInteger(token.count) || token.count < 0) {
      violations.push('INVALID_TOKEN_COUNT')
    }
  }

  // 3. Total token count constraints (only count tokens with count > 0)
  const totalTokenCount = userTokens.reduce((sum, t) => sum + t.count, 0)
  if (constraints.minTokens && totalTokenCount < constraints.minTokens) {
    violations.push(`TOO_FEW_TOKENS`)
  }
  if (constraints.maxTokens && totalTokenCount > constraints.maxTokens) {
    violations.push(`TOO_MANY_TOKENS`)
  }
  if (constraints.exactTokens && totalTokenCount !== constraints.exactTokens) {
    violations.push(`WRONG_TOKEN_COUNT`)
  }

  // 4. Bill/coin requirements
  const hasBill = userTokens.some((t) => {
    const denom = allowedDenoms.find(
      (d: MoneyDenom) => d.valueCents === t.valueCents,
    )
    return denom?.type === 'bill' && t.count > 0
  })
  if (constraints.requireBill && !hasBill) {
    violations.push('MISSING_REQUIRED_BILL')
  }
  if (constraints.forbidBill && hasBill) {
    violations.push('FORBIDDEN_BILL_USED')
  }

  // Check if amount is correct
  const amountCorrect = userTotalCents === targetCents
  const delta = Math.abs(userTotalCents - targetCents)

  // Determine if answer is fully correct (amount + constraints)
  const isCorrect = amountCorrect && violations.length === 0

  // Determine error type with direction info
  let errorType: string | undefined
  if (!isCorrect) {
    // Constraint violations take precedence
    if (violations.length > 0) {
      errorType = violations[0] // Report first violation
    } else if (!amountCorrect) {
      // Amount errors with direction
      if (userTotalCents < targetCents) {
        errorType = 'TOO_LOW'
      } else {
        errorType = 'TOO_HIGH'
      }

      // Add specific delta error types for common mistakes (keep direction)
      if (delta === 5)
        errorType = userTotalCents < targetCents ? 'TOO_LOW_5C' : 'TOO_HIGH_5C'
      else if (delta === 10)
        errorType =
          userTotalCents < targetCents ? 'TOO_LOW_10C' : 'TOO_HIGH_10C'
      else if (delta === 20)
        errorType =
          userTotalCents < targetCents ? 'TOO_LOW_20C' : 'TOO_HIGH_20C'
      else if (delta === 50)
        errorType =
          userTotalCents < targetCents ? 'TOO_LOW_50C' : 'TOO_HIGH_50C'
      else if (delta === 100)
        errorType =
          userTotalCents < targetCents ? 'TOO_LOW_100C' : 'TOO_HIGH_100C'
    }
  }

  // Distractor index based on error type
  let distractorIndex = 0
  if (!isCorrect) {
    if (userTotalCents < targetCents) {
      if (delta === 50) distractorIndex = 3
      else if (delta === 100) distractorIndex = 4
      else distractorIndex = 1
    } else {
      distractorIndex = 2
    }
  }
  return {
    isCorrect,
    distractorIndex,
    displayAnswer: formatMoneyDisplay(userTotalCents),
    correctAnswerDisplay: formatMoneyDisplay(targetCents),
    errorType,
    delta,
  }
}
const validateFillIn: ValidatorFn = (userAnswer, question) => {
  const config = question.data || {}
  const acceptedAnswers = config.acceptedAnswers || [
    String(question.correctAnswer),
  ]
  const caseSensitive = config.caseSensitive || false
  const userStr = String(userAnswer).trim()

  const isCorrect = acceptedAnswers.some((ans: string) => {
    const accepted = String(ans).trim()

    // 1. Direct string match (case-insensitive unless caseSensitive)
    if (
      caseSensitive
        ? accepted === userStr
        : accepted.toLowerCase() === userStr.toLowerCase()
    ) {
      return true
    }

    // 2. Numeric normalization: treat commas and dots as interchangeable decimal separators
    //    This handles "0.4" vs "0,4", "1.200" vs "1200", etc.
    const userNum = parseFloat(normalizeNumeric(userStr))
    const acceptedNum = parseFloat(normalizeNumeric(accepted))
    if (!isNaN(userNum) && !isNaN(acceptedNum)) {
      return Math.abs(userNum - acceptedNum) < 0.001
    }

    return false
  })

  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: userStr,
  }
}
const validateOrdering: ValidatorFn = (userAnswer, question) => {
  const userIndices = String(userAnswer).split(',').map(Number)
  const config = question.data || {}
  // Support both flat and nested data formats:
  // Flat: data.correctOrder   Nested: data.ordering.correctOrder
  const orderingData = config.ordering || config
  const correctOrder = orderingData.correctOrder || []
  const isCorrect =
    userIndices.length === correctOrder.length &&
    userIndices.every(
      (val: number, index: number) => val === correctOrder[index],
    )
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
  }
}
const validateMatching: ValidatorFn = (userAnswer, question) => {
  let userPairs: [number, number][] = []
  try {
    userPairs = JSON.parse(String(userAnswer))
  } catch {
    /* empty */
  }
  const config = question.data || {}
  // Support multiple data formats for expected pair count:
  // Format A: data.pairs   Format B: data.matching.pairs   Format C: data.leftItems
  const expectedLength =
    config.pairs?.length ||
    config.matching?.pairs?.length ||
    config.leftItems?.length ||
    0
  const isCorrect =
    userPairs.every(([left, right]) => left === right) &&
    userPairs.length === expectedLength
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
  }
}
const validateDragDropFill: ValidatorFn = (userAnswer, question) => {
  let userAnswers: Record<string, string> = {}
  try {
    userAnswers = JSON.parse(String(userAnswer))
  } catch {
    /* empty */
  }
  const config = question.data || {}
  let blanks: Array<{ id: string; correctAnswer: string }> = config.blanks || []

  // Format B: draggables + correctPlacements (no blanks array)
  if (blanks.length === 0 && config.draggables && config.correctPlacements) {
    const draggables = config.draggables as string[]
    const placements = config.correctPlacements as Record<string, number>
    blanks = Object.entries(placements).map(([id, dragIndex]) => ({
      id,
      correctAnswer: draggables[dragIndex] || '',
    }))
  }

  const isCorrect =
    blanks.length > 0 &&
    blanks.every((blank) => userAnswers[blank.id] === blank.correctAnswer)
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
  }
}
const validateTableReading: ValidatorFn = (userAnswer, question) => {
  const config = question.data || {}
  const isCorrect = Number(userAnswer) === config.correctSelection
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
  }
}
const validateNumberSequence: ValidatorFn = (userAnswer, question) => {
  const userValues = String(userAnswer)
    .split(',')
    .map((s) => s.trim())
  const config = question.data || {}
  const correctValues = (config.correctValues || []).map(String)

  // FIXED: Support multiple accepted formats for each answer
  const isCorrect =
    userValues.length === correctValues.length &&
    userValues.every((userVal: string, index: number) => {
      const correctVal = correctValues[index]

      // Direct string match (case-insensitive for text)
      if (userVal.toLowerCase() === correctVal.toLowerCase()) return true

      // Numeric comparison with tolerance for decimals
      const userNum = parseFloat(userVal.replace(/[€\s,]/g, '.'))
      const correctNum = parseFloat(correctVal.replace(/[€\s,]/g, '.'))
      if (!isNaN(userNum) && !isNaN(correctNum)) {
        // Allow small floating point tolerance (0.001)
        return Math.abs(userNum - correctNum) < 0.001
      }

      // Time format comparison (HH:MM)
      if (userVal.includes(':') && correctVal.includes(':')) {
        return userVal === correctVal
      }
      return false
    })
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: userValues.join(', '),
  }
}
const normalizeNumeric = (val: string): string => {
  let cleaned = val.trim().replace(/[€\s]/g, '')
  if (cleaned.includes('.') && cleaned.includes(',')) {
    cleaned = cleaned.replace(/\./g, '').replace(',', '.')
  } else if (cleaned.includes(',')) {
    cleaned = cleaned.replace(',', '.')
  } else if (cleaned.includes('.')) {
    if (/^\d{1,3}(\.\d{3})+$/.test(cleaned)) {
      cleaned = cleaned.replace(/\./g, '')
    }
  }
  return cleaned
}
const validateRatioTable: ValidatorFn = (userAnswer, question) => {
  let userInputs: Record<string, string> = {}
  try {
    userInputs = JSON.parse(String(userAnswer))
  } catch {
    /* empty */
  }
  const config = question.data || {}
  const rows = config.rows || []
  const hiddenCells: Array<{ row: number; col: number }> =
    config.hiddenCells || []
  const isCorrect = hiddenCells.every((cell) => {
    const key = `${cell.row}-${cell.col}`
    const userVal = userInputs[key] || ''
    const correctVal = String(rows[cell.row]?.[cell.col] || '')
    if (userVal.trim() === correctVal.trim()) return true
    const userNum = parseFloat(normalizeNumeric(userVal))
    const correctNum = parseFloat(normalizeNumeric(correctVal))
    if (!isNaN(userNum) && !isNaN(correctNum)) {
      return Math.abs(userNum - correctNum) < 0.001
    }
    return false
  })
  const displayParts = hiddenCells.map((cell) => {
    const key = `${cell.row}-${cell.col}`
    return userInputs[key] || '?'
  })
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: displayParts.join(', '),
  }
}
const validateTextHighlight: ValidatorFn = (userAnswer, question) => {
  const config = question.data || {}

  // Support both explicit segments AND text+correctHighlights format
  let segments: Array<{ id: string; text: string; isCorrect?: boolean }> =
    config.segments || []

  if (segments.length === 0 && config.text) {
    // Build segments from text + correctHighlights (same logic as renderer)
    const text: string = config.text
    const correctHighlights: Array<{ start: number; end: number }> =
      config.correctHighlights || []
    const wordRegex = /\S+/g
    let match: RegExpExecArray | null
    while ((match = wordRegex.exec(text)) !== null) {
      const wordStart = match.index
      const wordEnd = match.index + match[0].length
      const isCorrect = correctHighlights.some(
        (h: { start: number; end: number }) =>
          wordStart < h.end && wordEnd > h.start,
      )
      segments.push({
        id: `word-${wordStart}`,
        text: match[0],
        isCorrect,
      })
    }
  }

  const selectedSegmentIndex = segments.findIndex((s) => s.id === userAnswer)
  const selectedSegment = segments[selectedSegmentIndex]
  if (!selectedSegment)
    return {
      isCorrect: false,
      distractorIndex: 0,
    }
  return {
    isCorrect: selectedSegment.isCorrect === true,
    distractorIndex: selectedSegmentIndex,
    displayAnswer: selectedSegment.text,
  }
}
const validateBarChart: ValidatorFn = (userAnswer, question) => {
  const selectedIndex = Number(userAnswer)
  const config = question.data || {}
  const correctIndex = config.correctIndex

  // Normalize data: support both canonical {data: [{label, value}]} and legacy {labels: [], values: []}
  let data: Array<{ label: string; value: number }> = []
  if (Array.isArray(config.data) && config.data.length > 0) {
    data = config.data
  } else if (Array.isArray(config.labels) && Array.isArray(config.values)) {
    data = config.labels.map((label: string, i: number) => ({
      label,
      value: config.values[i] ?? 0,
    }))
  }

  const isCorrect = selectedIndex === correctIndex
  const selectedLabel = data[selectedIndex]?.label || '?'
  const correctLabel = data[correctIndex]?.label || '?'
  return {
    isCorrect,
    distractorIndex: selectedIndex,
    displayAnswer: isCorrect
      ? correctLabel
      : `${selectedLabel} (juiste antwoord: ${correctLabel})`,
    correctAnswerDisplay: correctLabel,
  }
}
const validateLineChart: ValidatorFn = (userAnswer, question) => {
  const selectedIndex = Number(userAnswer)
  const config = question.data || {}
  const correctIndex = config.correctIndex

  // Normalize data: support both canonical {data: [{label, value}]} and legacy {labels: [], values: []}
  let data: Array<{ label: string; value: number }> = []
  if (Array.isArray(config.data) && config.data.length > 0) {
    data = config.data
  } else if (Array.isArray(config.labels) && Array.isArray(config.values)) {
    data = config.labels.map((label: string, i: number) => ({
      label,
      value: config.values[i] ?? 0,
    }))
  }

  const isCorrect = selectedIndex === correctIndex
  const selectedLabel = data[selectedIndex]?.label || '?'
  const correctLabel = data[correctIndex]?.label || '?'
  return {
    isCorrect,
    distractorIndex: selectedIndex,
    displayAnswer: isCorrect
      ? correctLabel
      : `${selectedLabel} (juiste antwoord: ${correctLabel})`,
    correctAnswerDisplay: correctLabel,
  }
}
const validatePieChart: ValidatorFn = (userAnswer, question) => {
  const selectedIndex = Number(userAnswer)
  const qData = question.data || {}
  const correctIndex = qData.correctIndex
  const labels = qData.labels || []
  const isCorrect = selectedIndex === correctIndex
  const selectedLabel = labels[selectedIndex] || '?'
  const correctLabel = labels[correctIndex] || '?'
  return {
    isCorrect,
    distractorIndex: selectedIndex,
    displayAnswer: isCorrect
      ? correctLabel
      : `${selectedLabel} (juiste antwoord: ${correctLabel})`,
    correctAnswerDisplay: correctLabel,
  }
}
const validateScheduleReading: ValidatorFn = (userAnswer, question) => {
  const config = question.data || {}
  const correctCell = config.correctCell
  const schedule = config.schedule || {
    columns: [],
    timeSlots: [],
    events: [],
  }
  if (!correctCell)
    return {
      isCorrect: false,
      distractorIndex: 0,
    }
  const [userRow, userCol] = String(userAnswer).split('-').map(Number)
  const isCorrect = userRow === correctCell.row && userCol === correctCell.col
  const findEvent = (row: number, col: number) =>
    schedule.events?.find((e: ScheduleEvent) => e.row === row && e.col === col)
  const userEvent = findEvent(userRow, userCol)
  const correctEvent = findEvent(correctCell.row, correctCell.col)
  const userLabel =
    userEvent?.content || `rij ${userRow + 1}, kolom ${userCol + 1}`
  const correctLabel =
    correctEvent?.content ||
    `rij ${correctCell.row + 1}, kolom ${correctCell.col + 1}`
  const distractorKey = userRow * schedule.columns.length + userCol
  return {
    isCorrect,
    distractorIndex: distractorKey,
    displayAnswer: isCorrect
      ? correctLabel
      : `${userLabel} (juiste antwoord: ${correctLabel})`,
    correctAnswerDisplay: correctLabel,
  }
}

const validateMultiplicationGrid: ValidatorFn = (userAnswer, question) => {
  let userInputs: Record<string, string> = {}
  try {
    userInputs = JSON.parse(String(userAnswer))
  } catch {
    /* empty */
  }

  const config = question.data || {}
  const mode = config.mode || 'single_table'

  let allCorrect = true
  let totalCells = 0
  let correctCells = 0

  if (mode === 'single_table') {
    const baseNumber = config.baseNumber || 1
    const range: number[] = config.range || []
    const hiddenCells: number[] = config.hiddenCells || []
    totalCells = hiddenCells.length

    allCorrect = hiddenCells.every((cellIndex: number) => {
      const multiplier = range[cellIndex]
      const correctValue = baseNumber * multiplier
      const userVal = userInputs[String(cellIndex)] || ''
      const userNum = parseFloat(normalizeNumeric(userVal))
      const isMatch =
        !isNaN(userNum) && Math.abs(userNum - correctValue) < 0.001
      if (isMatch) correctCells++
      return isMatch
    })
  } else {
    // cross_table
    const rows: number[] = config.rows || []
    const cols: number[] = config.cols || []
    const hiddenCells: Array<{ row: number; col: number }> =
      config.hiddenCells || []
    totalCells = hiddenCells.length

    allCorrect = hiddenCells.every((cell: { row: number; col: number }) => {
      const correctValue = rows[cell.row] * cols[cell.col]
      const key = `${cell.row}-${cell.col}`
      const userVal = userInputs[key] || ''
      const userNum = parseFloat(normalizeNumeric(userVal))
      const isMatch =
        !isNaN(userNum) && Math.abs(userNum - correctValue) < 0.001
      if (isMatch) correctCells++
      return isMatch
    })
  }

  return {
    isCorrect: allCorrect,
    distractorIndex: allCorrect ? undefined : 0,
    displayAnswer: `${correctCells}/${totalCells} correct`,
  }
}

const validateTrueFalseSeries: ValidatorFn = (userAnswer, question) => {
  let userAnswers: boolean[] = []
  try {
    userAnswers = JSON.parse(String(userAnswer))
  } catch {
    /* empty */
  }

  const config = question.data || {}
  const statements = config.statements || []

  let correctCount = 0
  const isCorrect =
    userAnswers.length === statements.length &&
    statements.every((stmt: { isTrue: boolean }, idx: number) => {
      const match = userAnswers[idx] === stmt.isTrue
      if (match) correctCount++
      return match
    })

  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: `${correctCount}/${statements.length} correct`,
  }
}

const validateEstimationSlider: ValidatorFn = (userAnswer, question) => {
  const userValue = Number(userAnswer)
  const config = question.data || {}
  const correctValue = config.correctValue || 0
  const tolerancePercent = config.tolerancePercent || 10
  const max = config.max || 100
  const min = config.min || 0

  const range = max - min
  const tolerance = range * (tolerancePercent / 100)
  const isCorrect = Math.abs(userValue - correctValue) <= tolerance

  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: String(userValue),
    correctAnswerDisplay: String(correctValue),
  }
}

const validateCategorize: ValidatorFn = (userAnswer, question) => {
  let userAssignments: Record<string, number> = {}
  try {
    userAssignments = JSON.parse(String(userAnswer))
  } catch {
    /* empty */
  }

  const config = question.data || {}
  const items: Array<{ text: string; correctCategory: number }> =
    config.items || []

  let correctCount = 0
  const isCorrect =
    Object.keys(userAssignments).length === items.length &&
    items.every((item, idx) => {
      const match = userAssignments[String(idx)] === item.correctCategory
      if (match) correctCount++
      return match
    })

  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: `${correctCount}/${items.length} correct`,
  }
}

const validateErrorSpotting: ValidatorFn = (userAnswer, question) => {
  let userCorrections: Record<number, string> = {}
  try {
    userCorrections = JSON.parse(String(userAnswer))
  } catch {
    /* empty */
  }

  const config = question.data || {}
  const errors: Array<{
    word: string
    position: number
    correction: string
    rule?: string
  }> = config.errors || []

  let correctCount = 0

  // Check if they found all errors and corrected them properly
  const isCorrect =
    Object.keys(userCorrections).length === errors.length &&
    errors.every((error) => {
      const userCorrection = userCorrections[error.position]
      if (!userCorrection) return false

      // Case-insensitive comparison
      const match =
        userCorrection.toLowerCase().trim() ===
        error.correction.toLowerCase().trim()
      if (match) correctCount++
      return match
    })

  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: `${correctCount}/${errors.length} fouten verbeterd`,
  }
}

const validateGridShade: ValidatorFn = (userAnswer, question) => {
  let selectedIndices: number[] = []
  try {
    selectedIndices = JSON.parse(String(userAnswer))
  } catch {
    /* empty */
  }

  const config =
    question.data?.gridShade || (question.data as Record<string, unknown>) || {}
  const targetCount = config.targetCount || 0

  const isCorrect = selectedIndices.length === targetCount

  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: `${selectedIndices.length} vakjes gekleurd`,
  }
}

const validateStepSolver: ValidatorFn = (userAnswer, question) => {
  let userSteps: Record<string, string | number> = {}
  try {
    userSteps = JSON.parse(String(userAnswer))
  } catch {
    /* empty */
  }

  const config =
    question.data?.stepSolver ||
    (question.data as Record<string, unknown>) ||
    {}
  const steps = config.steps || []

  let correctCount = 0

  const isCorrect =
    steps.length > 0 &&
    steps.every((step: any, index: number) => {
      const userVal = String(userSteps[index] || '').trim()
      const accepted = step.acceptedAnswers || [step.correctAnswer]

      const match = accepted.some((ans: string | number) => {
        const acceptedStr = String(ans).trim()
        if (acceptedStr.toLowerCase() === userVal.toLowerCase()) return true

        const userNum = parseFloat(normalizeNumeric(userVal))
        const acceptedNum = parseFloat(normalizeNumeric(acceptedStr))
        if (!isNaN(userNum) && !isNaN(acceptedNum)) {
          return Math.abs(userNum - acceptedNum) < 0.001
        }
        return false
      })

      if (match) correctCount++
      return match
    })

  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: `${correctCount}/${steps.length} stappen correct`,
  }
}

const validateSentenceParser: ValidatorFn = (userAnswer, question) => {
  let userAssignments: Record<string, string> = {}
  try {
    userAssignments = JSON.parse(String(userAnswer))
  } catch {
    /* empty */
  }

  const config =
    question.data?.sentenceParser ||
    (question.data as Record<string, unknown>) ||
    {}
  const correctAssignments = config.correctAssignments || {}

  const correctKeys = Object.keys(correctAssignments)
  let correctCount = 0

  const isCorrect =
    correctKeys.length > 0 &&
    correctKeys.every((key) => {
      const match = userAssignments[key] === correctAssignments[key]
      if (match) correctCount++
      return match
    }) &&
    Object.keys(userAssignments).length === correctKeys.length

  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: `${correctCount}/${correctKeys.length} woorden correct benoemd`,
  }
}

const validateComparisonJudge: ValidatorFn = (userAnswer, question) => {
  const selectedIndex = Number(userAnswer)
  const config =
    question.data?.comparisonJudge ||
    (question.data as Record<string, unknown>) ||
    {}
  const correctIndex = config.correctIndex ?? -1

  const isCorrect = selectedIndex === correctIndex
  const items = config.items || []
  const selectedLabel = items[selectedIndex]?.content || '?'
  const correctLabel = items[correctIndex]?.content || '?'

  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : selectedIndex,
    displayAnswer: isCorrect
      ? correctLabel
      : `${selectedLabel} (juiste antwoord: ${correctLabel})`,
    correctAnswerDisplay: correctLabel,
  }
}

const validateVerbConjugationFlow: ValidatorFn = (userAnswer, question) => {
  return {
    isCorrect:
      String(userAnswer).trim().toLowerCase() ===
      String(question.correctAnswer).trim().toLowerCase(),
  }
}

const validateThermometerSlider: ValidatorFn = (userAnswer, question) => {
  return {
    isCorrect: Number(userAnswer) === Number(question.correctAnswer),
  }
}

const validateSentenceErrorJudge: ValidatorFn = (userAnswer, question) => {
  const selectedIndex = Number(userAnswer)
  const config = question.data || {}
  const sentences: Array<{
    text: string
    hasError: boolean
    correction?: string
  }> =
    (config.sentences as Array<{
      text: string
      hasError: boolean
      correction?: string
    }>) || []

  if (selectedIndex < 0 || selectedIndex >= sentences.length) {
    return { isCorrect: false, distractorIndex: 0 }
  }

  const selected = sentences[selectedIndex]
  const isCorrect = selected?.hasError === true

  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : selectedIndex,
    displayAnswer: selected?.text || '?',
  }
}

// ============================================================
// REGISTRY EXPORT
// ============================================================

const VALIDATOR_REGISTRY: Record<QuestionType, ValidatorFn> = {
  multiple_choice: validateMultipleChoice,
  cijferend_rekenen: validateColumnArithmetic,
  cijferend_delen: validateLongDivision,
  clock: validateClock,
  money: validateMoney,
  fill_in: validateFillIn,
  ordering: validateOrdering,
  matching: validateMatching,
  drag_drop_fill: validateDragDropFill,
  table_reading: validateTableReading,
  number_sequence: validateNumberSequence,
  ratio_table: validateRatioTable,
  text_highlight: validateTextHighlight,
  bar_chart: validateBarChart,
  line_chart: validateLineChart,
  pie_chart: validatePieChart,
  schedule_reading: validateScheduleReading,
  multiplication_grid: validateMultiplicationGrid,
  true_false_series: validateTrueFalseSeries,
  estimation_slider: validateEstimationSlider,
  categorize: validateCategorize,
  error_spotting: validateErrorSpotting,
  grid_shade: validateGridShade,
  step_solver: validateStepSolver,
  sentence_parser: validateSentenceParser,
  comparison_judge: validateComparisonJudge,
  verb_conjugation_flow: validateVerbConjugationFlow,
  thermometer_slider: validateThermometerSlider,
  sentence_error_judge: validateSentenceErrorJudge,
}

/**
 * Get the validator function for a question type.
 * Falls back to multiple_choice validator if type is unknown.
 */
export function getValidator(questionType: string): ValidatorFn {
  if (questionType === 'geld_samenstellen' || questionType === 'klok_aflezen') {
    throw new Error(
      `Obsolete questionType "${questionType}" detected. Migrate to canonical "money" or "clock".`,
    )
  }
  const validator = VALIDATOR_REGISTRY[questionType]
  if (!validator) {
    throw new Error(
      `Unsupported questionType "${questionType}" in validatorRegistry.`,
    )
  }
  return validator
}

/**
 * Validate an answer using the appropriate validator.
 * This is the primary API for answer validation.
 */
export function validateAnswer(
  questionType: string,
  userAnswer: string | number,
  question: Question,
): ValidationResult {
  const validator = getValidator(questionType)
  return validator(userAnswer, question)
}
